import { createStyles, Grid } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import withStyles, { WithStyles } from '@material-ui/core/styles/withStyles';
import { SvgIconProps } from '@material-ui/core/SvgIcon';
import { capitalize } from '@material-ui/core/utils/helpers';
import Typography from '@serko/zui/es/Typography';
import classNames from 'classnames';
import React, { ComponentType, FunctionComponent, ReactNode } from 'react';
import compose from 'recompose/compose';
import pure from 'recompose/pure';
import { SegmentTypes } from '../../models/itinerary';

interface OwnProps {
  header: ReactNode;
  icon: ComponentType<SvgIconProps>;
  type: SegmentTypes;
}

interface SectorProps extends OwnProps, WithStyles<typeof styles> {}

const SectorBase: FunctionComponent<SectorProps> = ({
  children,
  classes,
  header,
  icon: Icon,
  type,
}) => {
  return (
    <div
      className={classNames(
        classes.root,
        classes[`type${capitalize(type)}` as TypeClassKey],
      )}
    >
      <div className={classes.header}>
        <Grid container alignItems="center" alignContent="center" spacing={16}>
          <Grid item>
            <Icon />
          </Grid>
          <Grid item xs>
            <Typography variant="title2" color="inherit" align="left">
              {header}
            </Typography>
          </Grid>
        </Grid>
      </div>
      <div className={classes.body}>{children}</div>
    </div>
  );
};

// styled
type TypeClassKey = 'typeAir' | 'typeHotel' | 'typeCar';

const styles = (theme: Theme) =>
  createStyles({
    body: {
      padding: theme.spacing.unit * 2,
    },
    root: {
      backgroundColor: theme.zeno.travelModes.air.lightest,
      borderRadius: theme.shape.borderRadius,
      overflow: 'hidden',
    },
    header: {
      display: 'flex',
      boxSizing: 'border-box',
      height: theme.spacing.unit * 7,
      color: theme.zeno.text.light.main,
      paddingLeft: theme.spacing.unit * 3,
      paddingRight: theme.spacing.unit * 3,
      paddingTop: theme.spacing.unit * 2,
      paddingBottom: theme.spacing.unit * 2,
      width: '100%',
    },
    typeAir: {
      backgroundColor: theme.zeno.travelModes.air.lightest,
      '& $header': {
        backgroundColor: theme.zeno.travelModes.air.main,
        color: theme.zeno.travelModes.air.contrastText,
      },
    },
    typeHotel: {
      backgroundColor: theme.zeno.travelModes.accommodation.lightest,
      '& $header': {
        backgroundColor: theme.zeno.travelModes.accommodation.main,
        color: theme.zeno.travelModes.accommodation.contrastText,
      },
    },
    typeCar: {
      backgroundColor: theme.zeno.travelModes.vehicle.lightest,
      '& $header': {
        backgroundColor: theme.zeno.travelModes.vehicle.main,
        color: theme.zeno.travelModes.vehicle.contrastText,
      },
    },
  });

const enhance = compose<SectorProps, OwnProps>(
  pure,
  withStyles(styles),
);

export const Sector = enhance(SectorBase);

Sector.displayName = 'Sector';
